import * as React from 'react';
import { LocationContainer } from './components/LocationContainer';
import { GeoTagsContainer } from './components/GeoTagsContainer';
import { useSteps } from './utils/useSteps';
import { EStepField } from './constants/stepField';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { IGeoFormValue } from './types';
import { useGeoFormValue } from './utils/useGeoFormValue';
import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { pathOr } from 'ramda';
import { useSelector } from 'react-redux';
import { selectDomainLocation } from '../../selectors/selectDomainLocation';

export const GeoContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, extras },
    answersState,
    setAnswersState,
    setNextStep,
    registerBackInterceptor,
    unRegisterBackInterceptor,
  } = props;

  const domainLocation = useSelector(selectDomainLocation);
  const [value, onChange] = React.useState(
    pathOr<IGeoFormValue>({ location: domainLocation }, [answerField, 'value'], answersState),
  );
  const onSubmit = React.useCallback(
    () =>
      setAnswersState(prevState => {
        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: { answerField, value },
        };
        setNextStep(nextAnswersState);

        return nextAnswersState;
      }),
    [value, setAnswersState, setNextStep, answerField],
  );

  const { updateSteps, currentStep, nextStep } = useSteps({
    geoExtras: extras,
    onSubmit,
    answersState,
    registerBackInterceptor,
    unRegisterBackInterceptor,
  });

  const { onGeoValuesChange, onLocationChange } = useGeoFormValue({
    value,
    onChange,
    currentStep,
    updateSteps,
  });

  return (
    <QuestionLayoutContainer
      title={currentStep.title}
      description={currentStep.description}
      disableSubmit={!value.location}
    >
      {currentStep.field === EStepField.location && (
        <LocationContainer
          location={value.location}
          onLocationChange={onLocationChange}
          onSubmit={nextStep}
          inputName={answerField}
        />
      )}
      {value.location && currentStep.field !== EStepField.location && (
        <GeoTagsContainer
          key={currentStep.field}
          location={value.location}
          stepConfig={currentStep}
          onGeoValuesChange={onGeoValuesChange}
          geoValues={value.geoValues?.[currentStep.field] || []}
          onSubmit={nextStep}
          inputName={answerField}
        />
      )}
    </QuestionLayoutContainer>
  );
};

GeoContainer.displayName = 'GeoContainer';
